import * as d3 from "d3";
import _, { values } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { Button, Segment, Table } from "semantic-ui-react";
import { PlotChannelColors, PlotChannelDeepColors } from "../../models/common_model";
import { LimitData, MeasurementData } from "../../models/testing_model";

export function ArrayPlotComponent(props: {
    data: MeasurementData[]
}) {
    const { data: allData } = props;

    const currentInfo = useState<{
        x: number,
        y: number[],
        upper: number[],
        lower: number[],
        outOfLimit: number[],
        isShow: boolean
    }>({ x: 0, y: [0, 0], upper: [0, 0], lower: [0, 0], outOfLimit: [0, 0], isShow: false })

    const elementRef = useRef<HTMLDivElement>(null);

    const channels = _.uniq(allData.map(d => d.channel)).sort();
    const previousIndexes = _.uniq(allData.map(d => d.index)).sort();

    const [data, setData] = useState<MeasurementData[]>(allData);
    const currentAttempts = useState(_.max(previousIndexes));

    const sSelectedChannel = useState<boolean[]>(channels.map(c => true));

    const channelColors: string[][] = channels.map((channel, index) => [PlotChannelColors[index], PlotChannelDeepColors[index]]);

    const selectedChannelColors = channelColors.filter((c, index) => sSelectedChannel[0][index]);

    const margin = ({ top: 20, right: 30, bottom: 50, left: 50 });

    useEffect(() => {
        setData(allData.filter(d => d.index === currentAttempts[0]));
    }, [allData, currentAttempts[0]])

    useEffect(() => {
        const selectedChannels = channels.filter((channel, index) => sSelectedChannel[0][index]);
        if (selectedChannels.length === 0) {
            return
        }

        const width = elementRef.current!.offsetWidth;
        const height = 300;

        const svg = d3.select(elementRef.current!)
            .append("svg")
            .attr("viewBox", [0, 0, width, height] as any);

        const firstChannelData = data.find(d => selectedChannels.includes(d.channel))!;
        const firstResult = firstChannelData.result as number[];

        let gpibChannelNames: string[] = [];
        if (selectedChannels.length === 1) {
            const channelData = data.find(d => d.channel == selectedChannels[0])!;
            gpibChannelNames = (JSON.parse(channelData.gpib_channel_names || '[]') as string[]).map(n => {
                const ns = n.split('#');
                if (ns[1].toLowerCase() !== 'none') {
                    return ns[1];
                } else {
                    return `Port ${ns[0]}`;
                }
            });
        }

        const xDomain = [1, firstResult.length];
        const x = d3.scaleLinear().domain(xDomain).range([margin.left, width - margin.right]);
        const xAxis = (g: any) => {
            const axis = d3.axisBottom(x as any).ticks(firstResult.length).tickSizeInner(-height + margin.top + margin.bottom);
            if (gpibChannelNames.length > 0 && gpibChannelNames.length === firstResult.length) {
                axis.tickFormat((v, i) => gpibChannelNames[i]);
            }

            g.attr("transform", `translate(0,${height - margin.bottom})`)
                .call(axis).selectAll("text")  
                .style("text-anchor", "end")
                .attr("dx", "-.8em")
                .attr("dy", ".15em")
                .attr("transform", "rotate(-65)" );
        }

        svg.append("g")
            .attr("class", "x-axis-inner")
            .call(xAxis);

        let yDomain: number[] = [];
        const channelResults: number[][] = [];
        const channelLimits: LimitData<number[]>[] = [];
        for (const channel of selectedChannels) {
            const channelData = data.find(d => d.channel == channel)!;
            const result = channelData.result as number[];
            const limits = channelData.limits as LimitData<number[]>;
            yDomain = yDomain.concat(result);
            if (limits.lower) {
                yDomain = yDomain.concat(limits.lower.filter(_.isNumber));
            }
            if (limits.upper) {
                yDomain = yDomain.concat(limits.upper.filter(_.isNumber));
            }
            channelResults.push(result);
            channelLimits.push(limits);
        }
        yDomain = d3.extent(yDomain) as number[];
        const y = d3.scaleLinear().domain(yDomain).range([height - margin.bottom, margin.top])

        const yAxis = (g: any) => {
            g.attr("transform", `translate(${margin.left},0)`).call(d3.axisLeft(y).tickSizeInner(-width + margin.left + margin.right));
        }

        svg.append("g")
            .attr("class", "y-axis-inner")
            .call(yAxis);

        selectedChannels.forEach((channel, index) => {
            const result = channelResults[index];
            const limits = channelLimits[index];

            const lineValues: number[][] = [];
            const uppers: number[][] = [];
            const lowers: number[][] = [];
            result.forEach((v, i) => {
                const x = i + 1;
                if (_.isNumber(v)) {
                    lineValues.push([x, v]);
                } else {
                    lineValues.push([x, yDomain[0] - 1]);
                }
                if (limits.upper && _.isNumber(limits.upper[i])) {
                    uppers.push([x, limits.upper[i]]);
                }
                if (limits.lower && _.isNumber(limits.lower[i])) {
                    lowers.push([x, limits.lower[i]]);
                }
            });


            svg.append("g")
                .selectAll('dot')
                .data(lineValues)
                .enter()
                .append('circle')
                .attr('cx', (d, i) => x(d[0]))
                .attr('cy', (d) => y(d[1]))
                .attr('r', 5)
                .style('fill', (d, i) => (limits.lower && d[1] > limits.lower[i]) && (limits.upper && d[1] < limits.upper[i]) ? channelColors[index][0] : 'rgba(255, 0, 0, 0.3)')

            const line = (d3.line() as any)
                .x(function (d: number[], i: number) { return x(d[0]) })
                .y(function (d: number[], i: number) { return y(d[1]) })//.curve(d3.curveBasis)

            svg.append("path")
                .attr('class', 'data-line')
                .attr("fill", "none")
                .attr("stroke", 'red')
                .attr("stroke-width", 1)
                .attr("d", line(uppers));

            svg.append("path")
                .attr('class', 'data-line')
                .attr("fill", "none")
                .attr("stroke", 'red')
                .attr("stroke-width", 1)
                .attr("d", line(lowers))

        });


        //focus
        const xValues = firstResult.map((d, i) => i + 1)
        const focus = svg.append('g').style('display', 'none');

        focus.append('line')
            .attr('id', 'focusLineX')
            .attr('class', 'focusLine');
        selectedChannels.forEach((channel, index) => {
            focus.append('circle')
                .attr('id', 'focusCircle_' + index)
                .attr('r', 5)
                .attr('class', 'circle focusCircle');

            focus.append('line')
                .attr('id', 'focusLineY_' + index)
                .attr('class', 'focusLine');
        });

        const bisectDate = d3.bisector(function (d) { return d }).left;
        svg.append('rect')
            .attr('class', 'overlay')
            .attr('width', width)
            .attr('height', height)
            .on('mouseover', function () { focus.style('display', null); })
            .on('mouseout', function () {
                focus.style('display', 'none');
                currentInfo[1](info => ({ ...info, isShow: false }))
            })
            .on('mousemove', function (event) {
                const mouse = d3.pointer(event);
                const mouseX = x.invert(mouse[0]);
                let i = bisectDate(xValues, mouseX); // returns the index to the current data item

                const d0 = xValues[i - 1];
                const d1 = xValues[i];
                // work out which date value is closest to the mouse
                if (mouseX - d0 < d1 - mouseX) {
                    i -= 1;
                }

                if (!xValues[i] || !channelResults.find(vs => _.isNumber(vs[i]))) {
                    const emptyValues: number[] = selectedChannels.map(c => 0);
                    currentInfo[1](v => ({
                        isShow: false,
                        x: 0,
                        y: emptyValues,
                        lower: emptyValues,
                        upper: emptyValues,
                        outOfLimit: emptyValues
                    }))
                } else {
                    currentInfo[1](() => {
                        const yValue: number[] = [];
                        const lower: number[] = [];
                        const upper: number[] = [];
                        const outOfLimit: number[] = [];

                        channelResults.map((result, cIndex) => {
                            const v = result[i];
                            const limits = channelLimits[cIndex];
                            const l = limits.lower ? limits.lower[i] : NaN;
                            const u = limits.upper ? limits.upper[i] : NaN;
                            yValue.push(v);
                            lower.push(l);
                            upper.push(u);

                            let outOfLimitValue = 0
                            if (!isNaN(l) && v < l) {
                                outOfLimitValue = l - v;
                            } else if (!isNaN(u) && v > u) {
                                outOfLimitValue = v - u
                            }

                            outOfLimit.push(outOfLimitValue);
                        })

                        return {
                            isShow: true,
                            x: xValues[i],
                            y: yValue,
                            lower,
                            upper,
                            outOfLimit
                        }
                    });

                    const xv = x(xValues[i]);

                    focus.select('#focusLineX')
                        .attr('x1', xv).attr('y1', y(yDomain[0]))
                        .attr('x2', xv).attr('y2', y(yDomain[1]));

                    channelResults.forEach((result, index) => {
                        const yv = y(result[i]);

                        focus.select('#focusCircle_' + index)
                            .attr('cx', xv)
                            .attr('cy', yv);

                        focus.select('#focusLineY_' + index)
                            .attr('x1', x(xDomain[0])).attr('y1', yv)
                            .attr('x2', x(xDomain[1])).attr('y2', yv);
                    });
                }
            });

        return () => {
            svg.remove()
        }

    }, [sSelectedChannel[0], data]);

    return <div>
        {previousIndexes.length > 1 && <>
            <Button.Group size="mini" compact>
                {previousIndexes.map(i => <Button key={i} size="mini" basic={currentAttempts[0] !== i} onClick={e => currentAttempts[1](i)}>Attempts {i}</Button>)}
            </Button.Group>
        </>}
        <div style={{ padding: '1rem 1rem 1rem 4rem' }}>
            X: {currentInfo[0].x.toFixed(2)} &nbsp;&nbsp;
            {channels.filter((channel, index) => sSelectedChannel[0][index]).map((channel, index) => <div key={channel} style={{ background: selectedChannelColors[index][1], padding: '0.5rem', color: '#ddd' }}>
                <div  style={{ visibility: currentInfo[0].isShow ? 'visible' : 'hidden' }}>
                    <b style={{ color: '#fff' }}>CH{channel}:</b>&nbsp;&nbsp;
                    <span style={{ color: ((!_.isNil(currentInfo[0].lower[index]) && currentInfo[0].y[index] < currentInfo[0].lower[index]) || (!_.isNil(currentInfo[0].upper[index]) && currentInfo[0].y[index] >= currentInfo[0].upper[index])) ? 'red' : '' }}>Y: {currentInfo[0].y[index]?.toExponential(3)}</span> &nbsp;&nbsp;
                    {!_.isNil(currentInfo[0].upper[index]) && <>Upper: {currentInfo[0].upper[index]} &nbsp;&nbsp;</>}
                    {!_.isNil(currentInfo[0].lower[index]) && <>Lower: {currentInfo[0].lower[index]} &nbsp;&nbsp;</>}
                    Out Of Limit: {currentInfo[0].outOfLimit[index]?.toExponential(3)} &nbsp;&nbsp;
                </div>
            </div>)}
        </div>
        <div ref={elementRef} />
        <Button.Group size="mini" style={{ paddingLeft: `${margin.left}px` }}>
            {channels.map((channel, index) => <Button style={{ background: sSelectedChannel[0][index] ? channelColors[index][0] : null }} color={'grey'} onClick={() => sSelectedChannel[1](vs => vs.map((v, i) => index === i ? !v : v))} key={channel}>Channel{channel}</Button>)}
        </Button.Group>
    </div>
}